import { downloadWeekBackup } from './backup';
import { playerFirstName, type PlayerId } from './roster';
import {
  createStore,
  hydrateGameIntel,
  loadEspnWeek,
  loadSeasonAdjustments,
  loadSeasonWeeks,
  mergeSeasonWeeks,
  retryEspn,
} from './store';
import { applyTheme, loadTheme, type BoardTheme } from './theme';
import { renderApp, syncThemeChips, type UiHandles } from './ui';
import type { WeekState } from './types';
import { parseWeekNumber, parseWeekYear } from './weeks';
import { skeletonHtml } from './motion/states';

export async function boot(): Promise<void> {
  const root = document.querySelector<HTMLElement>('#app');
  if (!root) throw new Error('#app missing');

  applyTheme(loadTheme());
  root.innerHTML = skeletonHtml();

  const { store, espnError } = await createStore();
  let error = espnError;
  const handles: UiHandles = { root };
  let seasonWeeks: WeekState[] = [];
  let seasonAdjustments: Partial<Record<PlayerId, number>> = {};
  const liveWeek = parseWeekNumber(store.getWeek().weekKey) || 1;
  const liveYear = parseWeekYear(store.getWeek().weekKey);

  const paint = (): void => {
    const current = store.getWeek();
    const others = seasonWeeks.filter((week) => week.weekKey !== current.weekKey);
    renderApp(store, handles, {
      espnError: error,
      liveWeek,
      seasonWeeks: mergeSeasonWeeks(current, others),
      seasonAdjustments,
      onRetry: () => void handleRetry(true),
      onBackup: () => downloadWeekBackup(store.getWeek()),
      onReset: () => {
        if (parseWeekNumber(store.getWeek().weekKey) < liveWeek) return;
        const first = playerFirstName(store.getActivePlayer());
        if (
          !window.confirm(
            `Clear ${first}'s picks for this week? Games that already started will stay.`,
          )
        ) {
          return;
        }
        void store.resetActiveSheet();
      },
      onTheme: (theme: BoardTheme) => {
        applyTheme(theme);
        syncThemeChips(root.querySelectorAll<HTMLButtonElement>('[data-theme-pick]'), theme);
      },
      onWeek: (week) => void handleWeek(week),
    });
  };

  const refreshSeason = async (): Promise<void> => {
    [seasonWeeks, seasonAdjustments] = await Promise.all([
      loadSeasonWeeks(store),
      loadSeasonAdjustments(),
    ]);
    paint();
  };

  const handleRetry = async (summaries = false): Promise<void> => {
    error = await retryEspn(store, { summaries });
    paint();
  };

  const handleWeek = async (week: number): Promise<void> => {
    if (parseWeekNumber(store.getWeek().weekKey) === week) return;
    error = await loadEspnWeek(store, week, liveYear);
    if (!error) void hydrateGameIntel(store).catch(() => undefined);
    if (!error) void refreshSeason();
    paint();
  };

  store.subscribe(() => paint());
  paint();
  void hydrateGameIntel(store).catch(() => undefined);
  void refreshSeason();

  setInterval(() => void handleRetry(false), 60_000);
}
